// =============================================
// useBoundaries — hook for Semantic Boundary Declaration
// =============================================

import { useState, useCallback } from "react";
import {
  buildBoundaries,
  getBoundaries,
  checkBoundaryViolations,
  type BoundaryViolation,
} from "@/services/validation/boundaryEnforcer";
import type { SemanticBoundary, ThematicNode } from "@/types/thematicMaps";
import { useToast } from "@/hooks/use-toast";

export function useBoundaries() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boundaries, setBoundaries] = useState<SemanticBoundary | null>(null);
  const [violations, setViolations] = useState<BoundaryViolation[]>([]);
  const { toast } = useToast();

  const fetchBoundaries = useCallback(async (mapId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBoundaries(mapId);
      setBoundaries(result);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Błąd pobierania granic";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const generateBoundaries = useCallback(
    async (mapId: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await buildBoundaries(mapId);
        setBoundaries(result);
        toast({
          title: "Granice wygenerowane",
          description: `Owned: ${result.owned.length}, Not owned: ${result.not_owned.length}, Adjacent: ${result.adjacent.length}`,
        });
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Błąd generowania granic";
        setError(message);
        toast({
          title: "Błąd generowania granic",
          description: message,
          variant: "destructive",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const checkViolations = useCallback(
    (nodes: ThematicNode[]) => {
      if (!boundaries) return [];
      const result = checkBoundaryViolations(nodes, boundaries);
      setViolations(result);
      return result;
    },
    [boundaries],
  );

  return {
    loading,
    error,
    boundaries,
    violations,
    fetchBoundaries,
    generateBoundaries,
    checkViolations,
  };
}
